<script setup lang="ts">
import {
  BoxComponent,
  ButtonComponent,
  type Campaign,
  CAMPAIGN_PERFORMANCE_MAX_DATE_RANGE_DAYS,
  CampaignCreatorsActiveStatusFilter,
  CampaignHashtags,
  CampaignOverviewCreators,
  CampaignOverviewFilterTargetEnum,
  CampaignOverviewPerformance,
  CampaignStatusEnum,
  copyTextToClipboard,
  FlexComponent,
  getActiveSocialMetric,
  getDefaultRangeOption,
  ImageIcon,
  PauseIcon,
  PlayIcon,
  TooltipComponent,
  TypographyComponent,
} from '@metapic-apps/shared'
import { ElDropdown, ElDropdownItem, ElDropdownMenu, ElIcon } from 'element-plus'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { useGetCampaignCreatorsCount, useGetCampaignCreatorsListRequest, useSetCampaignActivityRequest } from '@/api/campaign.api'
import { useFinanceCampaignPerformance } from '@/api/finance-campaign-performance.api'
import { useSocialMediaCampaignChart, useSocialMediaCampaignMetricTotals } from '@/api/social-media-campaign-performance.api'
import DeleteIcon from '@/assets/icons/delete-button.svg?component'
import EditIcon from '@/assets/icons/edit-pen.svg?component'
import GroupIcon from '@/assets/icons/group.svg?component'
import LinkIcon from '@/assets/icons/link.svg?component'
import MoreIcon from '@/assets/icons/more.svg?component'
import { config, isDemoMode } from '@/lib/helpers'

const props = withDefaults(defineProps<{ campaign: Campaign; editable?: boolean }>(), { editable: true })
const emit = defineEmits<{ delete: [] }>()

const router = useRouter()

const status = computed(() => props.campaign.status as CampaignStatusEnum)

const inviteLink = computed(() => {
  if (isDemoMode || !props.campaign.token || !config.creatorAppUrl) {
    return undefined
  }
  const url = new URL(`${config.creatorAppUrl}/campaign-invitation`)
  url.searchParams.set('join-campaign', props.campaign.token)
  return url.toString()
})

const { setStatus, loading: isStatusLoading } = useSetCampaignActivityRequest(props.campaign.id)

const dateRange = ref<{ startDate: string; endDate: string }>(
  getDefaultRangeOption(props.campaign.constraints?.validFrom ?? props.campaign.createdAt, props.campaign.constraints?.validUntil),
)
const activeFilterTarget = ref<CampaignOverviewFilterTargetEnum>(CampaignOverviewFilterTargetEnum.Clicks)

const {
  data: campaignStats,
  isLoading: isCampaignStatsLoading,
  isChartLoading: isCampaignStatsChartLoading,
} = useFinanceCampaignPerformance(props.campaign.storeId, props.campaign.id, activeFilterTarget, dateRange)
const { totals: socialMetricTotals } = useSocialMediaCampaignMetricTotals(props.campaign.storeId, props.campaign.id, dateRange)
const { points: socialChartPoints, isLoading: isSocialChartLoading } = useSocialMediaCampaignChart(
  props.campaign.storeId,
  props.campaign.id,
  computed(() => getActiveSocialMetric(activeFilterTarget.value)),
  dateRange,
)

const creatorsActiveFilter = ref<CampaignCreatorsActiveStatusFilter>(CampaignCreatorsActiveStatusFilter.Active)
const creatorsSearchQuery = ref('')
const creatorsCurrentPage = ref(1)
const creatorsSortField = ref('clicks')
const creatorsSortOrder = ref<'asc' | 'desc'>('desc')

const {
  data: creatorsList,
  totalPages: creatorsTotalPages,
  isLoading: isCreatorsListLoading,
} = useGetCampaignCreatorsListRequest({
  id: props.campaign.id,
  status: creatorsActiveFilter,
  dateRange,
  page: creatorsCurrentPage,
  searchQuery: creatorsSearchQuery,
  sortBy: { field: creatorsSortField, order: creatorsSortOrder },
})
const { data: creatorsCountStats, isLoading: isCreatorsStatsLoading } = useGetCampaignCreatorsCount({
  id: props.campaign.id,
  dateRange,
})

const isCampaignActive = computed(() => status.value === CampaignStatusEnum.Active || status.value === CampaignStatusEnum.Scheduled)
const isPauseBtnShown = computed(() =>
  [CampaignStatusEnum.Active, CampaignStatusEnum.Paused, CampaignStatusEnum.Scheduled].includes(status.value),
)

const setCampaignActiveStatus = async () => {
  await setStatus(isCampaignActive.value)
}

const handleCopyInviteLink = async () => {
  if (inviteLink.value) {
    await copyTextToClipboard(inviteLink.value)
  }
}

const goToManage = () => router.push({ name: 'campaign-users', params: { id: props.campaign.id } })

const goToContentLibrary = () =>
  router.push({ name: 'content-library', query: { campaignId: props.campaign.id, campaignName: props.campaign.campaignTitle } })

const goToEdit = () => router.push({ name: 'campaign-editing', params: { id: props.campaign.id } })
</script>
<template>
  <BoxComponent>
    <FlexComponent direction="column" :gap="24">
      <TypographyComponent variant="headline-medium">{{ campaign.campaignTitle }}</TypographyComponent>
      <CampaignHashtags v-if="campaign.hashtags?.length" :hashtags="campaign.hashtags" />
      <FlexComponent align="center" :gap="8" wrap="wrap">
        <ButtonComponent variant="accent" class="campaign-overview__action-btn" @click="goToManage">
          <template #iconLeft>
            <ElIcon size="18" color="var(--primary)" class="campaign-overview__icon">
              <GroupIcon />
            </ElIcon>
          </template>
          Manage
        </ButtonComponent>
        <TooltipComponent v-if="inviteLink" trigger="click" :auto-close="1000">
          <ButtonComponent variant="accent" class="campaign-overview__action-btn" @click="handleCopyInviteLink">
            <template #iconLeft>
              <ElIcon size="18" color="var(--primary)" class="campaign-overview__icon">
                <LinkIcon />
              </ElIcon>
            </template>
            Copy invite link
          </ButtonComponent>
          <template #content>Copied!</template>
        </TooltipComponent>
        <ButtonComponent v-if="editable" variant="accent" class="campaign-overview__action-btn" @click="goToContentLibrary">
          <template #iconLeft>
            <ElIcon size="18" color="var(--primary)" class="campaign-overview__icon">
              <ImageIcon />
            </ElIcon>
          </template>
          Content library
        </ButtonComponent>
        <ElDropdown v-if="editable" trigger="click" placement="bottom-end" :teleported="false">
          <ButtonComponent is-icon variant="secondary">
            <MoreIcon />
          </ButtonComponent>
          <template #dropdown>
            <ElDropdownMenu>
              <ElDropdownItem v-if="isPauseBtnShown" :disabled="isStatusLoading" @click="setCampaignActiveStatus">
                <FlexComponent align="center" :gap="8">
                  <ElIcon :size="16" color="var(--primary)" class="campaign-overview__icon">
                    <PauseIcon v-if="isCampaignActive" />
                    <PlayIcon v-else />
                  </ElIcon>
                  <TypographyComponent variant="body-large">{{ isCampaignActive ? 'Pause' : 'Un-pause' }}</TypographyComponent>
                </FlexComponent>
              </ElDropdownItem>
              <ElDropdownItem @click="goToEdit">
                <FlexComponent align="center" :gap="8">
                  <ElIcon :size="16">
                    <EditIcon />
                  </ElIcon>
                  <TypographyComponent variant="body-large">Edit</TypographyComponent>
                </FlexComponent>
              </ElDropdownItem>
              <ElDropdownItem @click="emit('delete')">
                <FlexComponent align="center" :gap="8">
                  <ElIcon :size="16" color="var(--error)">
                    <DeleteIcon />
                  </ElIcon>
                  <TypographyComponent variant="body-large" color="var(--error)">Delete</TypographyComponent>
                </FlexComponent>
              </ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </FlexComponent>
    </FlexComponent>
    <BoxComponent :mt="24">
      <CampaignOverviewPerformance
        v-model:date-range="dateRange"
        v-model:active-filter-target="activeFilterTarget"
        view-mode="advertiser"
        :max-date-range-days="CAMPAIGN_PERFORMANCE_MAX_DATE_RANGE_DAYS"
        :currency="campaign.currency"
        :campaign-stats="campaignStats"
        :is-campaign-stats-loading="isCampaignStatsLoading"
        :is-stats-chart-loading="isCampaignStatsChartLoading"
        :social-metric-totals="socialMetricTotals"
        :social-chart-points="socialChartPoints"
        :is-social-chart-loading="isSocialChartLoading"
      />
    </BoxComponent>
    <BoxComponent :mt="24">
      <BoxComponent :mb="16">
        <TypographyComponent variant="title-large">Creators</TypographyComponent>
      </BoxComponent>
      <CampaignOverviewCreators
        v-model:active-filter="creatorsActiveFilter"
        v-model:search-query="creatorsSearchQuery"
        v-model:current-page="creatorsCurrentPage"
        v-model:sort-field="creatorsSortField"
        v-model:sort-order="creatorsSortOrder"
        :creators-list="creatorsList"
        :is-list-loading="isCreatorsListLoading"
        :is-stats-loading="isCreatorsStatsLoading"
        :total-pages="creatorsTotalPages"
        :count-stats="creatorsCountStats"
        :currency="campaign.currency"
        :max-clicks="campaign.constraints?.maxClicks"
        view-mode="advertiser"
      >
        <template #creator-name="{ creator }">
          <RouterLink :to="{ name: 'creator', params: { id: creator.userId } }">
            <TypographyComponent variant="title-medium" text-truncate>{{ creator.displayName || '' }}</TypographyComponent>
          </RouterLink>
        </template>
      </CampaignOverviewCreators>
    </BoxComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
:deep(.el-dropdown-menu__item) {
  padding: 16px;
}
.campaign-overview__action-btn {
  line-height: initial;
}
.campaign-overview__icon :deep(path) {
  fill: currentColor;
}
:deep(.search-input input) {
  height: initial;
}
:deep(.search-input) {
  display: flex;
  align-items: center;
}
</style>
